import React, { Fragment } from "react";
import {
  Dialog,
  DialogPanel,
  DialogTitle,
  Transition,
  TransitionChild,
} from "@headlessui/react";
import { Button } from "@/components/ui/button";
import { UpdatableOrderStatus } from "../../api/updateProductStoreOrderStatus/types";
import { ORDER_STATUS_STYLES } from "../../utils/orderStatusStyles";

interface UpdateOrderStatusConfirmationProps {
  isOpen: boolean;
  confirmationNumber: string;
  targetStatus: UpdatableOrderStatus | null;
  loading: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

const UpdateOrderStatusConfirmation: React.FC<
  UpdateOrderStatusConfirmationProps
> = ({
  isOpen,
  confirmationNumber,
  targetStatus,
  loading,
  onConfirm,
  onCancel,
}) => {
  const style = targetStatus ? ORDER_STATUS_STYLES[targetStatus] : null;

  return (
    <Transition as={Fragment} show={isOpen}>
      <Dialog as="div" className="relative z-40" onClose={onCancel}>
        <TransitionChild
          as={Fragment}
          enter="transition-opacity ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="transition-opacity ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-30" />
        </TransitionChild>

        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <TransitionChild
              as={Fragment}
              enter="transform transition ease-out duration-300"
              enterFrom="scale-95"
              enterTo="scale-100"
              leave="transform transition ease-in duration-200"
              leaveFrom="scale-100"
              leaveTo="scale-95"
            >
              <DialogPanel className="w-full max-w-md transform rounded-lg bg-white dark:bg-slate-800 p-6 text-left shadow-xl transition-all">
                <DialogTitle className="text-lg font-medium leading-6 text-gray-900 dark:text-white">
                  Update order status
                </DialogTitle>
                <div className="mt-3 text-sm text-gray-700 dark:text-gray-300">
                  <p>
                    Are you sure you want to mark order{" "}
                    <span className="font-mono font-semibold">
                      {confirmationNumber}
                    </span>{" "}
                    as{" "}
                    <span className="font-semibold">
                      {style?.label.toLowerCase() ?? targetStatus}
                    </span>
                    ?
                  </p>
                </div>

                <div className="mt-5 flex justify-end space-x-3">
                  <Button
                    disabled={loading}
                    onClick={onCancel}
                    type="button"
                    variant="outline"
                  >
                    Cancel
                  </Button>
                  <Button
                    disabled={loading || !style}
                    onClick={onConfirm}
                    type="button"
                  >
                    {loading ? "Updating…" : `Mark as ${style?.label ?? ""}`}
                  </Button>
                </div>
              </DialogPanel>
            </TransitionChild>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default UpdateOrderStatusConfirmation;
